import { Reveal } from "../components/Reveal";
import { FOUNDER_IMAGE, ORG, ABOUT_GAP_IMAGE } from "../lib/constants";
import { Link } from "react-router-dom";
import { ArrowRight, Handshake, HeartPulse, Sprout, Users } from "lucide-react";

export default function AboutPage() {
  const partners = [
    {
      icon: Handshake,
      title: "McIntosh Foundation",
      body: "Together we provide a suitable and safe living environment for women and children.",
    },
    {
      icon: HeartPulse,
      title: "Phoenix Counseling",
      body: "Quality clinical counseling woven into the fabric of our program.",
    },
    {
      icon: Users,
      title: "Local Community",
      body: "Connecting residents with resources, jobs, and relationships that last.",
    },
    {
      icon: Sprout,
      title: "Celebrate Recovery & Local Churches",
      body: "Weekly worship, Bible study, and Celebrate Recovery nourish the spirit.",
    },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative pt-20 pb-16 md:pt-28 md:pb-24 bg-gradient-to-br from-brand-cream via-brand-bone/30 to-brand-cream">
        <div className="container-editorial grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
          <Reveal className="md:col-span-7">
            <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold">About Us</div>
            <h1 className="mt-3 font-serif text-5xl md:text-6xl lg:text-7xl text-brand-charcoal tracking-tight leading-[1.02]">
              A faith-based home for women ready for a new beginning.
            </h1>
          </Reveal>
          <Reveal delay={120} className="md:col-span-5">
            <p className="text-lg text-brand-muted leading-relaxed">
              Hosea Ministries Foundation is a Christ-centered transitional and supportive living home in Tahlequah,
              Oklahoma — a place where women recovering from addiction or incarceration can find safety, structure,
              and the community they need to rebuild.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Our Why */}
      <section className="py-24">
        <div className="container-editorial grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-5 order-2 md:order-1">
            <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold">Our Why</div>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl text-brand-charcoal tracking-tight leading-tight">
              There is a gap. We step into it.
            </h2>
            <p className="mt-5 text-brand-muted leading-relaxed">
              Rural northeast Oklahoma faces a deep resource deficiency — there are few places for women and families
              to access the housing and stability required to work on self improvement. This gap pushes more children
              into state custody and more women into repeated incarceration.
            </p>
            <p className="mt-4 text-brand-muted leading-relaxed">
              Our goal is to positively impact the lives of families in rural communities who are neglected and
              underfunded. With your help, we partner with the community to bring awareness and prevent generational
              poverty — so families can lead healthier lives.
            </p>
            <div className="mt-8">
              <a
                href={ORG.donateUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="about-donate-btn"
                className="inline-flex items-center gap-2 bg-brand-teal text-white rounded-full px-6 py-3 font-medium hover:bg-brand-tealDark transition-all hover:-translate-y-0.5"
              >
                Help fund the mission <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </Reveal>
          <Reveal delay={120} className="md:col-span-7 order-1 md:order-2">
            <div className="relative">
              <div className="absolute -inset-3 rounded-[2rem] bg-brand-teal/10 -rotate-1" />
              <div className="relative rounded-[1.75rem] overflow-hidden aspect-[4/3] bg-brand-bone shadow-lg">
                <img
                  src={ABOUT_GAP_IMAGE}
                  alt="Hosea Ministries Foundation merchandise and Bible"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Partners */}
      <section className="py-20 bg-white border-y border-brand-line">
        <div className="container-editorial">
          <Reveal>
            <div className="max-w-2xl">
              <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold">Our Partners</div>
              <h2 className="mt-3 font-serif text-4xl md:text-5xl text-brand-charcoal tracking-tight">
                We don't walk alone.
              </h2>
              <p className="mt-4 text-brand-muted">
                Lasting change happens through partnership. These organizations help us provide a whole-person
                program — body, mind, and spirit.
              </p>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {partners.map((p, i) => (
              <Reveal key={p.title} delay={i * 80}>
                <div className="bg-brand-cream rounded-2xl p-7 border border-brand-line hover:-translate-y-1 hover:shadow-lg transition-all duration-500 flex gap-5">
                  <span className="w-12 h-12 rounded-xl bg-brand-teal/10 text-brand-teal flex items-center justify-center shrink-0">
                    <p.icon className="w-5 h-5" />
                  </span>
                  <div>
                    <h3 className="font-serif text-2xl text-brand-charcoal">{p.title}</h3>
                    <p className="mt-2 text-brand-muted leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Founder story */}
      <section className="py-24">
        <div className="container-editorial grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 rounded-[2rem] bg-brand-peach/25 rotate-2" />
              <div className="relative rounded-[1.75rem] overflow-hidden aspect-[3/4] bg-brand-bone shadow-lg">
                <img src={FOUNDER_IMAGE} alt="Nikki Hendricks, Founder" className="w-full h-full object-cover" />
              </div>
            </div>
          </Reveal>
          <Reveal delay={120} className="md:col-span-7">
            <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold">The Founder</div>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl text-brand-charcoal tracking-tight leading-tight">
              Nikki Hendricks
            </h2>
            <p className="mt-5 text-brand-muted leading-relaxed text-lg">
              Nikki's story is stitched with both brokenness and grace — and that's exactly what makes Hosea
              Ministries a home rather than an institution. She knows what it feels like to need a second chance,
              and she's dedicated her life to creating that chance for other women.
            </p>
            <p className="mt-4 text-brand-muted leading-relaxed">
              Today she leads our team with a pastor's heart and a sister's compassion, welcoming every woman with
              the belief that restoration is possible.
            </p>
            <div className="mt-8">
              <Link
                to="/apply"
                data-testid="about-apply-btn"
                className="inline-flex items-center gap-2 bg-brand-teal text-white rounded-full px-6 py-3 font-medium hover:bg-brand-tealDark transition-all hover:-translate-y-0.5"
              >
                Start your application <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Recent praise */}
      <section className="py-20 bg-brand-bone/50">
        <div className="container-editorial">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center">
              <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold">Recent Praise</div>
              <h2 className="mt-3 font-serif text-4xl md:text-5xl text-brand-charcoal tracking-tight">
                Today, we celebrate progress.
              </h2>
              <p className="mt-5 text-brand-muted leading-relaxed">
                Every milestone, big or small, deserves recognition. Consistency. Courage. Commitment. Recovery is
                happening every day inside these walls.
              </p>
              <a
                href={ORG.facebookUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="facebook-link"
                className="mt-8 inline-flex items-center gap-2 text-brand-teal font-medium link-underline"
              >
                Follow our journey on Facebook <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
